import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, ArrowRight, Settings, Layers, Database, Zap } from "lucide-react";

export const ServicesPage = () => {
  const capabilities = [
    {
      id: "operations",
      icon: Settings,
      name: "Operations",
      tagline: "Structure behind the day-to-day.",
      items: [
        "Administrative coordination", "Research", "Documentation", "Process organization",
        "Task management", "Project coordination", "Workflow management", "Reporting",
        "SOP development", "Operational execution"
      ]
    },
    {
      id: "systems",
      icon: Layers,
      name: "Systems",
      tagline: "Connected systems. Organized information.",
      items: [
        "Process mapping", "Workflow design", "System organization", "Tool implementation",
        "System integration", "Process optimization", "Technology organization",
        "Information flow", "SOP architecture"
      ]
    },
    {
      id: "crm",
      icon: Database,
      name: "CRM",
      tagline: "From data to actionable pipelines.",
      items: [
        "CRM setup and organization", "Database management", "Pipeline management",
        "Lead management", "Data cleanup", "Lead categorization", "Follow-up systems",
        "CRM workflow configuration", "CRM maintenance"
      ]
    },
    {
      id: "automation",
      icon: Zap,
      name: "Automation",
      tagline: "Less repetition. Better workflows.",
      items: [
        "Workflow automation", "Lead routing", "Automated follow-ups", "Task automation",
        "Notifications", "Trigger-based workflows", "Platform integrations",
        "Repetitive-process reduction"
      ]
    }
  ];

  return (
    <div>
      {/* Hero */}
      <section className="py-20 bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl text-[#2D4A6D] mb-6 font-serif">Operational Services</h1>
          <div className="w-16 h-0.5 bg-[#2D4A6D] mx-auto mb-6" />
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Operational support across systems, workflows, CRM, automation, and execution.
          </p>
        </div>
      </section>

      {/* Capability Sections */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-6">
            {capabilities.map((cap, i) => (
              <motion.div key={i} id={cap.id} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }} className="scroll-mt-24">
                <div className="p-8 border border-slate-200 hover:border-[#2D4A6D]/50 transition-colors">
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
                    <div className="flex-1">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="w-12 h-12 bg-[#2D4A6D] flex items-center justify-center flex-shrink-0">
                          <cap.icon className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h3 className="text-xl font-semibold text-[#2D4A6D]">{cap.name}</h3>
                          <p className="text-sm text-slate-500">{cap.tagline}</p>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2 mt-4">
                        {cap.items.map((item, j) => (
                          <span key={j} className="inline-flex items-center gap-1 text-xs text-slate-600 bg-slate-100 px-3 py-1">
                            <CheckCircle className="w-3 h-3 text-[#2D4A6D]" />{item}
                          </span>
                        ))}
                      </div>
                    </div>
                    <Link to="/book" className="flex-shrink-0">
                      <button className="px-6 py-3 flex items-center border-2 border-[#2D4A6D] text-[#2D4A6D] hover:bg-[#2D4A6D] hover:text-white transition-colors">
                        Discuss <ArrowRight className="ml-2 w-4 h-4" />
                      </button>
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Engagement Structure */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl text-[#2D4A6D] mb-4 font-serif">Engagement Structure</h2>
            <div className="w-16 h-0.5 bg-[#2D4A6D] mx-auto mb-6" />
            <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-4">
              Engagements are structured around operational requirements rather than predefined packages.
            </p>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Scope, systems, workflows, responsibilities, and level of involvement are determined based on the requirements identified during discovery.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#2D4A6D] text-white">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-3xl sm:text-4xl mb-6 font-serif">Discuss Requirements</h2>
          <div className="w-16 h-0.5 bg-white/30 mx-auto mb-8" />
          <p className="text-slate-300 mb-10">
            Schedule a strategy call to identify operational requirements and determine the appropriate engagement structure.
          </p>
          <Link to="/book">
            <button className="bg-white text-[#2D4A6D] hover:bg-slate-100 px-10 py-4 font-medium flex items-center mx-auto">
              Book a Strategy Call <ArrowRight className="ml-2 w-5 h-5" />
            </button>
          </Link>
        </div>
      </section>
    </div>
  );
};
